import { CouponIcon, PassIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import { Dayjs } from "dayjs";
import { mapValues } from "lodash";
import React from "react";
import { Audit, LineItemObject, Purchase, PurchaseItem, RewardCodeObject } from "types";
import { Currency } from "../../../Common/Currency";
import { TimelineItem } from "../TimelineItem";

interface DiscountCodeTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
    purchaseCommitedAt?: Dayjs;
}

export const DiscountCodeTimelineItem = ({
    purchase,
    item,
    audit,
    showDate = false,
    purchaseCommitedAt,
}: DiscountCodeTimelineItemProps) => {
    const matchedLineItem = DiscountCodeTimelineItem.supportsAudit(purchase, item, audit);
    if (!matchedLineItem) {
        return null;
    }

    if (purchaseCommitedAt && matchedLineItem.createdAt < purchaseCommitedAt) {
        return null;
    }

    const code = matchedLineItem.couponCode?.code ?? matchedLineItem.name;
    const { title, color } = getTitleAndColor(matchedLineItem);

    const Icon = getCodeType(matchedLineItem) === RewardCodeObject.MEMBERSHIP ? PassIcon : CouponIcon;

    return (
        <TimelineItem
            title={title}
            audit={matchedLineItem}
            icon={<Icon />}
            color={color}
            showDate={showDate}
            purchase={purchase}
        >
            <div className="space-x-2 text-gray-darker">
                <Currency shouldRemoveTrailingZeroes={false}>{Math.abs(matchedLineItem.amount)}</Currency>
                <Badge color="secondary" icon={<Icon />} className="no-translate uppercase">
                    {code}
                </Badge>
            </div>
        </TimelineItem>
    );
};

DiscountCodeTimelineItem.supportsAudit = (_purchase: Purchase, _item: PurchaseItem, _audit: Audit) => {
    let auditItem;
    if (Array.isArray(_audit.data.items)) {
        auditItem = _audit.data?.items?.find((item_: PurchaseItem) => item_.id === _item?.id);
    } else {
        for (const itemIndex in _audit.data.items) {
            if (_audit.data.items[itemIndex].id === _item.id) {
                auditItem = _audit.data.items[itemIndex];
            }
        }
    }

    if (!auditItem) {
        return null;
    }

    const lineItems = Object.values(auditItem?.lineItems ?? {});
    const matchedItem: any = lineItems.find((lineItem: any) => lineItem?.old?.couponCode || lineItem?.new?.couponCode);

    if (matchedItem?.new ?? matchedItem?.old) {
        const newLineItem = matchedItem?.new ?? matchedItem?.old;
        return {
            ...newLineItem,
            createdAt: _audit.createdAt,
            createdBy: _audit.createdBy,
            isOld: Boolean(matchedItem?.old),
        };
    }

    /**
     * Note: Whenever membership code (DiscountCodeLineItem) is voided, sometimes the `old` and `new` object are not coming properly in audits.
     * For these scenarios `linItem.object.old=discount_code_line_item` and `lineItem.object.new=SOME_OTHER_OBJECT`.
     * And this is same for all other properties. `LineItem.field` is updated by the fields of some other line item
     **/
    const removalMatchedItem: any = lineItems.find(
        (lineItem: any) => lineItem?.couponCode?.old && !lineItem?.couponCode?.new,
    );

    if (removalMatchedItem) {
        const newLineItem = mapValues(removalMatchedItem, (value) => value?.old);
        if (newLineItem.object !== LineItemObject.DISCOUNT_CODE) {
            return false;
        }

        return {
            ...newLineItem,
            createdAt: _audit.createdAt,
            createdBy: _audit.createdBy,
            isOld: true,
        };
    }

    return false;
};

const getTitleAndColor = (item: any) => {
    const codeTitle = getCodeType(item) === RewardCodeObject.COUPON ? "Coupon" : "Membership Pass";

    return item?.isOld
        ? { title: `${codeTitle} Removed`, color: "danger" }
        : { title: `${codeTitle} Applied`, color: "primary" };
};

const getCodeType = (item: any) => {
    return item?.coupon ? RewardCodeObject.COUPON : RewardCodeObject.MEMBERSHIP;
};
